// -----------------------------------------------------------------
// PKIEngine
// -----------------------------------------------------------------
import CustomEngine from './CustomEngine'
import PKIUtils from './PKIUtils'
import { arrayBufferToString, stringToArrayBuffer, toBase64 } from 'pvutils'
// -----------------------------------------------------------------
//
// -----------------------------------------------------------------
const asn1js = require('asn1js')
const pkijs = require('pkijs')
// -----------------------------------------------------------------
// Setup Engine by Customized Engine
// -----------------------------------------------------------------
const custom = new CustomEngine()
pkijs.setEngine(custom.name, custom.crypto, custom.subtle)
const crypto = pkijs.getCrypto()
// -----------------------------------------------------------------
// PKIEngine: It is Crypto Engine for PKI
// -----------------------------------------------------------------
export default class PKIEngine {
  // ---------------------------------------------------------------
  // constructor
  // ---------------------------------------------------------------
  constructor () {
    this.version = '1.0.0'
  }
  // ---------------------------------------------------------------
  // version():
  // ---------------------------------------------------------------
  version () {
    return this.version
  }
  // ---------------------------------------------------------------
  // formatPEM():
  // ---------------------------------------------------------------
  async formatPEM (pemString) {
    const PEM_STRING_LENGTH = pemString.length; const LINE_LENGTH = 64
    const wrapNeeded = PEM_STRING_LENGTH > LINE_LENGTH
    if (wrapNeeded) {
      let format = ''; let wrapIndex = 0
      for (let i = LINE_LENGTH; i < PEM_STRING_LENGTH; i += LINE_LENGTH) {
        format += pemString.substring(wrapIndex, i) + '\r\n'
        wrapIndex = i
      }
      format += pemString.substring(wrapIndex, PEM_STRING_LENGTH)
      return format
    } else {
      return pemString
    }
  }
  // ---------------------------------------------------------------
  // generateKey():
  // ---------------------------------------------------------------
  async generateKey (signAlgo, hashAlgo, keyOption) {
    // console.log('PKIEngine.generateKey():ENTER')
    // console.log('PKIEngine.generateKey():signAlgo=' + signAlgo)
    // console.log('PKIEngine.generateKey():hashAlgo=' + hashAlgo) // sha256
    // console.log('PKIEngine.generateKey():keyOption=', JSON.stringify(keyOption))
    const algo = pkijs.getAlgorithmParameters(signAlgo, 'generatekey') // ex. 'ECDSA'
    if ('hash' in algo.algorithm) {
      algo.algorithm.hash.name = hashAlgo
    }
    if (keyOption.type === 'RSA') {
      if ('modulusLength' in algo.algorithm) {
        algo.algorithm.modulusLength = keyOption.value
      }
    }
    if (keyOption.type === 'ECDSA') {
      if ('namedCurve' in algo.algorithm) {
        algo.algorithm.namedCurve = keyOption.value
      }
    }
    let keyPair = null
    try {
      keyPair = await crypto.subtle.generateKey(algo.algorithm, true, algo.usages)
      // console.log('PKIEngine.generateKey():keyPair=' + keyPair)
      // console.log('PKIEngine.generateKey():privateKey=' + keyPair.privateKey)
      // console.log('PKIEngine.generateKey():publicKey=' + keyPair.publicKey)
    } catch (error) {
      throw new Error(error)
    }
    // console.log('PKIEngine.generateKey():LEAVE')
    return keyPair
  }
  // ---------------------------------------------------------------
  // parseCSR():
  // ---------------------------------------------------------------
  async parseCSR (csr) {
    // console.log('PKIEngine.parseCSR():ENTER')
    // console.log('PKIEngine.parseCSR():csr=' + csr)
    let result = null
    try {
      let pem = csr
      pem = pem.replace('-----BEGIN CERTIFICATE REQUEST-----', '')
      pem = pem.replace('-----END CERTIFICATE REQUEST-----', '')
      pem = pem.replace(/^\n/gm, '')
      pem = pem.replace(/^\r/gm, '')
      // console.log('PKIEngine.parseCSR():pem=' + pem)
      let data = PKIUtils.b642ary(pem)
      const asn = await asn1js.fromBER(data)
      result = await new pkijs.CertificationRequest({ schema: asn.result })
    } catch (e) {
      // console.error('PKIEngine.parseCSR():Error = ' + e)
    }
    // console.log('PKIEngine.parseCSR():LEAVE')
    return result
  }
  // ---------------------------------------------------------------
  // verifyCSR():
  // ---------------------------------------------------------------
  async verifyCSR (csr) {
    // console.log('PKIEngine.verifyCSR():ENTER')
    // console.log(csr);
    let result = false
    let data = null
    if (csr instanceof ArrayBuffer) {
      // console.log('PKIEngine.verifyCSR():csr is ArrayBuffer')
      data = csr
    } else {
      // console.log('PKIEngine.verifyCSR():csr is String')
      let pem = csr
      pem = pem.replace('-----BEGIN CERTIFICATE REQUEST-----', '')
      pem = pem.replace('-----END CERTIFICATE REQUEST-----', '')
      pem = pem.replace(/^\n/gm, '')
      pem = pem.replace(/^\r/gm, '')
      // console.log('PKIEngine.verifyCSR():pem=' + pem)
      data = PKIUtils.b642ary(pem)
    }
    try {
      const asn = await asn1js.fromBER(data)
      const p10 = await new pkijs.CertificationRequest({ schema: asn.result })
      // console.log('PKIEngine.verifyCSR():asn = ' + asn)
      // console.log('PKIEngine.verifyCSR():p10 = ' + p10)
      const verify = await p10.verify()
      // console.log('PKIEngine.verifyCSR():verify = ' + verify)
      result = true
    } catch (e) {
      // console.error('PKIEngine.verifyCSR():Error = ' + e)
    }
    // console.log('PKIEngine.verifyCSR():LEAVE')
    return result
  }
  // ---------------------------------------------------------------
  // createPKCS10():
  // ---------------------------------------------------------------
  async createPKCS10 (signAlgo, hashAlgo, keyPair) {
    // console.log('PKIEngine.createPKCS10():ENTER')
    // console.log('PKIEngine.createPKCS10():keyPair=' + keyPair)
    let publicKey = keyPair.publicKey
    let privateKey = keyPair.privateKey
    let result = null
    // console.log('PKIEngine.createPKCS10():publicKey=' + publicKey)
    // console.log('PKIEngine.createPKCS10():privateKey=' + privateKey)
    try {
      const pkcs10 = await new pkijs.CertificationRequest()
      pkcs10.version = 0
      // TODO: double check whether we need all attributes below inside our CSR for sending to BE ?
      // await pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
      //   type: '2.5.4.6',
      //   value: new asn1js.PrintableString({ value: 'JP' })
      // }))
      // await pkcs10.subject.typesAndValues.push(new pkijs.AttributeTypeAndValue({
      //   type: '2.5.4.3',
      //   value: new asn1js.Utf8String({ value: 'Test' })
      // }))

      await pkcs10.subjectPublicKeyInfo.importKey(publicKey)
      // const spki = await pkcs10.subjectPublicKeyInfo.subjectPublicKey.valueBlock.valueHex
      // // console.log(spki)
      // const digest = await crypto.digest(
      //   { name: 'SHA-1' },
      //   spki // pkcs10.subjectPublicKeyInfo.subjectPublicKey.valueBlock.valueHex
      // )
      // console.log('PKIEngine.createPKCS10():digest = ' + digest)
      // console.log(digest)

      // const altNames = new pkijs.GeneralNames({
      //   names: [
      //     new pkijs.GeneralName({
      //       type: 1, // rfc822Name
      //       value: 'test@pkidev.internal'
      //     }),
      //     new pkijs.GeneralName({
      //       type: 2, // dNSName
      //       value: 'test.pkidev.internal'
      //     })
      //   ]
      // })
      // console.log('PKIEngine.createPKCS10():altNames = ' + altNames)
      // console.log(altNames)

      pkcs10.attributes = []
      // await pkcs10.attributes.push(new pkijs.Attribute({
      //   type: '1.2.840.113549.1.9.14', // pkcs-9-at-extensionRequest
      //   values: [(new pkijs.Extensions({
      //     extensions: [
      //       new pkijs.Extension({
      //         extnID: '2.5.29.14',
      //         critical: false,
      //         extnValue: (new asn1js.OctetString({ valueHex: digest })).toBER(false)
      //       }),
      //       // new pkijs.Extension({
      //       //   extnID: '2.5.29.17',
      //       //   critical: false,
      //       //   extnValue: altNames.toSchema().toBER(false)
      //       // }),
      //       new pkijs.Extension({
      //         extnID: '1.2.840.113549.1.9.7',
      //         critical: false,
      //         extnValue: (new asn1js.PrintableString({ value: 'challenge' })).toBER(false)
      //       })
      //     ]
      //   })).toSchema()]
      // }))
      // console.log('PKIEngine.createPKCS10():attributes = ' + pkcs10.attributes)
      await pkcs10.sign(privateKey, hashAlgo)
      // console.log('PKIEngine.createPKCS10():signed')
      // console.log(asn1)
      result = await pkcs10.toSchema().toBER(false)
    } catch (e) {
      // console.error('PKIEngine.createPKCS10():Error = ' + e)
    }
    // console.log('PKIEngine.createPKCS10():LEAVE')
    return result
  }
  // ---------------------------------------------------------------
  // generateCSR():
  // ---------------------------------------------------------------
  async generateCSR (signAlgo, hashAlgo, keyPair) {
    // console.log('PKIEngine.generateCSR():ENTER')
    // console.log('PKIEngine.generateCSR():signAlgo=' + signAlgo)
    // console.log('PKIEngine.generateCSR():hashAlgo=' + hashAlgo)
    // console.log('PKIEngine.generateCSR():keyPair=' + keyPair)
    let result = ''
    let ary = await this.createPKCS10(signAlgo, hashAlgo, keyPair)
    if (await this.verifyCSR(ary)) {
      const csr = await this.formatPEM(toBase64(arrayBufferToString(ary)))
      result += '-----BEGIN CERTIFICATE REQUEST-----\r\n'
      result += csr + '\r\n'
      result += '-----END CERTIFICATE REQUEST-----\r\n'
    }
    // console.log('PKIEngine.generateCSR():LEAVE')
    return result
  }
  // ---------------------------------------------------------------
  // generatePKCS12()
  // ---------------------------------------------------------------
  async generatePKCS12 (
    pkcs8Simpl,
    certObjectsArr,
    password,
    inputAlgorithm,
    inputHashAlgorithm,
    schema) {
    // console.log('PKIEngine.generatePKCS12():ENTER')
    // console.log('PKIEngine.generatePKCS12():pkcs8Simpl=' + pkcs8Simpl)
    // console.log('PKIEngine.generatePKCS12():certSimpl=' + JSON.stringify(certObjectsArr))
    // console.log('PKIEngine.generatePKCS12():password=' + password)
    // console.log('PKIEngine.generatePKCS12():inputAlgorithm=' + inputAlgorithm)
    // console.log('PKIEngine.generatePKCS12():inputHashAlgorithm=' + inputHashAlgorithm)
    let base64 = ''
    // region Initial variables
    let sequence = new Promise((resolve, reject) => {
      resolve()
    })

    const keyLocalIDBuffer = new ArrayBuffer(20)
    const keyLocalIDView = new Uint8Array(keyLocalIDBuffer)
    crypto.getRandomValues(keyLocalIDView)

    const certLocalIDBuffer = new ArrayBuffer(4)
    const certLocalIDView = new Uint8Array(certLocalIDBuffer)
    crypto.getRandomValues(certLocalIDView)

    const passwordConverted = stringToArrayBuffer(password)

    const contentEncryptionAlgorithm = (pkijs.getAlgorithmParameters(inputAlgorithm, 'encrypt')).algorithm
    if (!('name' in contentEncryptionAlgorithm)) {
      return Promise.reject(new Error(`No support for selected algorithm: ${inputAlgorithm}`))
    }

    const makeInternalValuesParameters = {
      password: passwordConverted,
      contentEncryptionAlgorithm,
      hmacHashAlgorithm: inputHashAlgorithm,
      pbeSchema: schema,
      iterationCount: 2048
    }

    const friendlyName = 'Private Key For PKCS12 Certificate'

    let certSafeBags = certObjectsArr.map((certObj, index) => {
      const bagAttributes = [
        new pkijs.Attribute({
          type: '1.2.840.113549.1.9.21', // localKeyID
          values: [
            new asn1js.OctetString({ valueHex: keyLocalIDBuffer })
          ]
        }),
        new pkijs.Attribute({
          type: '1.2.840.113549.1.9.20', // friendlyName
          values: [
            new asn1js.BmpString({ value: friendlyName })
          ]
        })
      ]

      // End-entity certificate with attributes only
      return index === 0
        ? new pkijs.SafeBag({
          bagId: '1.2.840.113549.1.12.10.1.3',
          bagValue: new pkijs.CertBag({
            parsedValue: certObj
          }),
          bagAttributes
        })
        : new pkijs.SafeBag({
          bagId: '1.2.840.113549.1.12.10.1.3',
          bagValue: new pkijs.CertBag({
            parsedValue: certObj
          })
        })
    })

    // console.log('pkcs12.digicertLike():certSafeBags=', certSafeBags)

    const keySafeBag = new pkijs.SafeBag({
      bagId: '1.2.840.113549.1.12.10.1.2',
      bagValue: new pkijs.PKCS8ShroudedKeyBag({
        parsedValue: pkcs8Simpl
      }),
      bagAttributes: [
        new pkijs.Attribute({
          type: '1.2.840.113549.1.9.21', // localKeyID
          values: [
            new asn1js.OctetString({ valueHex: keyLocalIDBuffer })
          ]
        }),
        new pkijs.Attribute({
          type: '1.2.840.113549.1.9.20', // friendlyName
          values: [
            new asn1js.BmpString({ value: friendlyName })
          ]
        })
      ]
    })

    const pkcs12 = new pkijs.PFX({
      parsedValue: {
        integrityMode: 0, // Password-Based Integrity Mode
        authenticatedSafe: new pkijs.AuthenticatedSafe({
          parsedValue: {
            safeContents: [
              {
                privacyMode: 0, // "No-privacy" Protection Mode
                value: new pkijs.SafeContents({
                  safeBags: [
                    ...certSafeBags,
                    keySafeBag
                  ]
                })
              }
            ]
          }
        })
      }
    })

    const { safeBags } = pkcs12.parsedValue.authenticatedSafe.parsedValue.safeContents[0].value
    // Encode internal values for "PKCS8ShroudedKeyBag" which is last one in array
    sequence = sequence.then(
      () => safeBags[safeBags.length - 1].bagValue.makeInternalValues(makeInternalValuesParameters)
    )

    // Encode internal values for all "SafeContents" first (create all "Privacy Protection" envelopes)
    sequence = sequence.then(
      () => pkcs12.parsedValue.authenticatedSafe.makeInternalValues({
        safeContents: [
          makeInternalValuesParameters
        ]
      })
    )

    // Encode internal values for "Integrity Protection" envelope
    sequence = sequence.then(
      () => pkcs12.makeInternalValues({
        password: passwordConverted,
        iterations: 2048,
        pbkdf2HashAlgorithm: inputHashAlgorithm,
        hmacHashAlgorithm: inputHashAlgorithm
      })
    )

    // Save encoded data
    await sequence.then(() => {
      let array = pkcs12.toSchema().toBER(false)
      base64 = toBase64(arrayBufferToString(array))
      // console.log("pkcs12.digicertLike():array=", array);
      // console.log("pkcs12.digicertLike():base64=", base64);
    })

    return base64
  }
}

export const engine = new PKIEngine()
